import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  MessageSquare,
  Search,
  Loader2,
  Mail,
  Phone,
  User,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  RefreshCcw,
  Bot,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status, t }) => {
  const config = {
    pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    assigned: { color: 'bg-blue-100 text-blue-800', icon: User },
    in_progress: { color: 'bg-purple-100 text-purple-800', icon: RefreshCcw },
    resolved: { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    closed: { color: 'bg-gray-100 text-gray-800', icon: XCircle }
  };
  
  const { color, icon: Icon } = config[status] || config.pending;
  
  return (
    <Badge className={`${color} gap-1.5 text-[12px] px-3 py-1 font-semibold`}>
      <Icon className="h-3.5 w-3.5" />
      {t(`conversations.status.${status}`) || status}
    </Badge>
  );
};

const PriorityBadge = ({ priority, t }) => {
  const config = {
    low: 'bg-gray-100 text-gray-700',
    normal: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700'
  };
  
  return (
    <Badge className={`${config[priority] || config.normal} text-[12px] px-3 py-1 font-semibold`}>
      {t(`conversations.priority.${priority}`) || priority}
    </Badge>
  );
};

const ChannelIcon = ({ channel }) => {
  if (channel === 'whatsapp') {
    return <div className="h-10 w-10 rounded-xl bg-green-100 flex items-center justify-center"><Phone className="h-5 w-5 text-green-600" /></div>;
  }
  return <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center"><Mail className="h-5 w-5 text-blue-600" /></div>;
};

export const ConversationalQueuesPage = () => {
  const { t } = useTranslation('workflows');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [executingTask, setExecutingTask] = useState(false);
  const messagesEndRef = useRef(null);
  const listRef = useRef(null);
  const observerRef = useRef(null);

  // Initial load
  useEffect(() => {
    resetAndFetch();
  }, [statusFilter, channelFilter, priorityFilter]);

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedConversation?.messages]);

  const resetAndFetch = () => {
    setConversations([]);
    setPage(1);
    setHasMore(true);
    fetchConversations(1, true);
  };

  const fetchConversations = async (pageNum = page, isReset = false) => {
    if (isReset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '20'
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (channelFilter !== 'all') params.append('channel', channelFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/api/workflows/conversations?${params}`);
      const newData = response.data.data;
      const totalPages = response.data.pagination.total_pages;
      
      if (isReset) {
        setConversations(newData);
      } else {
        setConversations(prev => [...prev, ...newData]);
      }
      
      setHasMore(pageNum < totalPages);
      setPage(pageNum);
    } catch (error) {
      toast.error(t('conversations.messages.loadError'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchConversations(page + 1, false);
    }
  }, [loadingMore, hasMore, page]);

  // Intersection Observer for infinite scroll
  const lastConversationRef = useCallback(node => {
    if (loading || loadingMore) return;
    
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    }, {
      root: listRef.current,
      rootMargin: '100px',
      threshold: 0.1
    });
    
    if (node) observerRef.current.observe(node);
  }, [loading, loadingMore, hasMore, loadMore]);

  const handleSearch = () => {
    resetAndFetch();
  };

  const viewConversation = async (conv) => {
    try {
      const response = await axios.get(`${API_URL}/api/workflows/conversations/${conv.id}`);
      setSelectedConversation(response.data);
    } catch (error) {
      toast.error(t('conversations.messages.loadDetailError'));
    }
  };

  const refreshConversation = async () => {
    if (!selectedConversation) return;
    try {
      const response = await axios.get(`${API_URL}/api/workflows/conversations/${selectedConversation.id}`);
      setSelectedConversation(response.data);
    } catch (error) {
      toast.error(t('conversations.messages.updateError'));
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;
    
    setSendingMessage(true);
    try {
      await axios.post(
        `${API_URL}/api/workflows/conversations/${selectedConversation.id}/messages`,
        null,
        { params: { content: messageInput, direction: 'outbound' } }
      );
      setMessageInput('');
      await refreshConversation();
      toast.success(t('conversations.messages.sendSuccess'));
    } catch (error) {
      toast.error(t('conversations.messages.sendError'));
    } finally {
      setSendingMessage(false);
    }
  };

  const executeTask = async (taskId) => {
    if (!selectedConversation) return;
    
    setExecutingTask(true);
    try {
      await axios.post(`${API_URL}/api/webhooks/execute-task/${selectedConversation.id}/${taskId}`);
      toast.success(t('conversations.messages.taskExecuting'));
      await refreshConversation();
    } catch (error) {
      toast.error(t('conversations.messages.taskError'));
    } finally {
      setExecutingTask(false);
    }
  };

  const continueFlow = async () => {
    if (!selectedConversation) return;
    
    setExecutingTask(true);
    try {
      const response = await axios.post(`${API_URL}/api/webhooks/continue-task/${selectedConversation.id}`);
      toast.success(response.data.message || t('conversations.messages.continueSuccess'));
      await refreshConversation();
    } catch (error) {
      toast.error(t('conversations.messages.continueError'));
    } finally {
      setExecutingTask(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (!selectedConversation) return;
    
    try {
      await axios.put(`${API_URL}/api/workflows/conversations/${selectedConversation.id}`, { status: newStatus });
      toast.success(t('conversations.messages.statusUpdated'));
      await refreshConversation();
      // Update the conversation in the list without full refresh
      setConversations(prev => prev.map(c => 
        c.id === selectedConversation.id ? { ...c, status: newStatus } : c
      ));
    } catch (error) {
      toast.error(t('conversations.messages.statusError'));
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6" data-testid="conversational-queues-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('conversations.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{t('conversations.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversations List */}
        <div className="lg:col-span-1 space-y-4">
          {/* Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={t('conversations.searchPlaceholder')}
                className="pl-10"
                data-testid="conv-search"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="text-xs" data-testid="status-filter">
                  <SelectValue placeholder={t('conversations.filters.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('conversations.filters.all')}</SelectItem>
                  <SelectItem value="pending">{t('conversations.status.pending')}</SelectItem>
                  <SelectItem value="in_progress">{t('conversations.status.in_progress')}</SelectItem>
                  <SelectItem value="resolved">{t('conversations.status.resolved')}</SelectItem>
                  <SelectItem value="closed">{t('conversations.status.closed')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="text-xs" data-testid="channel-filter">
                  <SelectValue placeholder={t('conversations.filters.channel')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('conversations.filters.all')}</SelectItem>
                  <SelectItem value="email">{t('conversations.channels.email')}</SelectItem>
                  <SelectItem value="whatsapp">{t('conversations.channels.whatsapp')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="text-xs" data-testid="priority-filter">
                  <SelectValue placeholder={t('conversations.filters.priority')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('conversations.filters.all')}</SelectItem>
                  <SelectItem value="urgent">{t('conversations.priority.urgent')}</SelectItem>
                  <SelectItem value="high">{t('conversations.priority.high')}</SelectItem>
                  <SelectItem value="normal">{t('conversations.priority.normal')}</SelectItem>
                  <SelectItem value="low">{t('conversations.priority.low')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Conversations List with Infinite Scroll */}
          <div 
            ref={listRef}
            className="h-[calc(100vh-320px)] overflow-y-auto pr-1"
            data-testid="conversations-list"
          >
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-[#E63946]" />
              </div>
            ) : conversations.length > 0 ? (
              <div className="space-y-2">
                {conversations.map((conv, index) => {
                  const isLast = index === conversations.length - 1;
                  return (
                    <Card 
                      key={conv.id}
                      ref={isLast ? lastConversationRef : null}
                      className={`cursor-pointer hover:shadow-md transition-all border-[var(--line)] ${
                        selectedConversation?.id === conv.id ? 'ring-2 ring-[#E63946] bg-[var(--red-soft,#FBE6E8)]' : 'bg-[var(--surface)]'
                      }`}
                      onClick={() => viewConversation(conv)}
                      data-testid={`conv-${conv.id}`}
                      style={{ borderRadius: 'var(--r-lg, 12px)' }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <ChannelIcon channel={conv.channel} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-semibold text-[15px] text-[var(--ink)] truncate">
                                {conv.customer_name || conv.sender_id}
                              </p>
                              <span className="text-[13px] text-[var(--ink-3)] whitespace-nowrap font-medium">
                                {formatTime(conv.last_message_at)}
                              </span>
                            </div>
                            <p className="text-[13px] text-[var(--ink-2)] truncate mt-1">
                              {conv.subject || conv.sender_id}
                            </p>
                            <div className="flex items-center gap-2 mt-3">
                              <StatusBadge status={conv.status} t={t} />
                              <PriorityBadge priority={conv.priority} t={t} />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                {/* Loading more indicator */}
                {loadingMore && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-[#E63946]" />
                  </div>
                )}
                
                {/* End of list indicator */}
                {!hasMore && conversations.length > 0 && (
                  <p className="text-center text-xs text-gray-400 py-3">
                    {t('conversations.noMoreConversations')}
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p>{t('conversations.noConversations')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Conversation Detail */}
        <div className="lg:col-span-2">
          {selectedConversation ? (
            <Card className="h-[calc(100vh-200px)] flex flex-col">
              {/* Header */}
              <CardHeader className="border-b py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ChannelIcon channel={selectedConversation.channel} />
                    <div>
                      <CardTitle className="text-base">
                        {selectedConversation.customer_name || selectedConversation.sender_id}
                      </CardTitle>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedConversation.customer_email || selectedConversation.sender_id}
                        {selectedConversation.customer_phone && ` • ${selectedConversation.customer_phone}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={refreshConversation}>
                      <RefreshCcw className="h-4 w-4" />
                    </Button>
                    <Select value={selectedConversation.status} onValueChange={updateStatus}>
                      <SelectTrigger className="w-32 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{t('conversations.status.pending')}</SelectItem>
                        <SelectItem value="in_progress">{t('conversations.status.in_progress')}</SelectItem>
                        <SelectItem value="resolved">{t('conversations.status.resolved')}</SelectItem>
                        <SelectItem value="closed">{t('conversations.status.closed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Task Status Bar */}
                {selectedConversation.active_task_id && (
                  <div className="mt-3 p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bot className="h-4 w-4 text-purple-600" />
                      <span className="text-sm text-purple-700 dark:text-purple-300">
                        {t('conversations.taskBar.activeTask')}: {selectedConversation.active_task_status || 'running'}
                      </span>
                    </div>
                    {selectedConversation.active_task_status === 'waiting_for_user' && (
                      <Button 
                        size="sm" 
                        onClick={continueFlow}
                        disabled={executingTask}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {executingTask ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1" />}
                        {t('conversations.taskBar.continue')}
                      </Button>
                    )}
                  </div>
                )}
              </CardHeader>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {selectedConversation.messages?.map((msg, idx) => (
                    <div 
                      key={msg.id || idx}
                      className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.direction === 'outbound' 
                            ? msg.sender_type === 'system' 
                              ? 'bg-purple-100 text-purple-900 dark:bg-purple-900/30 dark:text-purple-100'
                              : 'bg-[#E63946] text-white'
                            : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
                        }`}
                      >
                        {msg.sender_type === 'system' && (
                          <div className="flex items-center gap-1 text-xs mb-1 opacity-75">
                            <Bot className="h-3 w-3" />
                            {t('conversations.chat.aiAgent')}
                          </div>
                        )}
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.direction === 'outbound' && msg.sender_type !== 'system' ? 'text-white/70' : 'text-gray-400'
                        }`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Input */}
              <div className="border-t p-3">
                <div className="flex gap-2">
                  <Input
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder={t('conversations.chat.placeholder')}
                    disabled={sendingMessage}
                    data-testid="message-input"
                  />
                  <Button 
                    onClick={sendMessage} 
                    disabled={sendingMessage || !messageInput.trim()}
                    className="bg-[#E63946] hover:bg-[#d32836]"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="h-[calc(100vh-200px)] flex items-center justify-center">
              <div className="text-center text-gray-500 dark:text-gray-400">
                <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p className="text-lg font-medium">{t('conversations.selectConversation')}</p>
                <p className="text-sm">{t('conversations.selectConversationSubtitle')}</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationalQueuesPage;
